import { ENGINEERING_MODELS } from './models'
import { createRasterizer, IMAGE_SIZE } from './raster'
import { matchBlocks, type Block, type BlockMove } from './morph'

const CYCLE = 24
const COARSE_AT = 8
const MORPH_START = 10
const MORPH_END = 14
const MAX_CELL = 16
const smooth = (value: number) => {
  const t = Math.max(0, Math.min(1, value))
  return t * t * (3 - 2 * t)
}
const mix = (a: number, b: number, t: number) => a + (b - a) * t
const seeds = Float32Array.from({ length: IMAGE_SIZE ** 2 }, (_, i) => {
  const value = Math.sin((i % IMAGE_SIZE) * 127.1 + Math.floor(i / IMAGE_SIZE) * 311.7) * 43758.5453
  return value - Math.floor(value)
})

/** Fine images coarsen into persistent blocks, which rearrange to build the next image. */
export function createEngineeringRenderer() {
  const rasterize = createRasterizer()
  if (!rasterize) return () => {}
  const stride = IMAGE_SIZE + 1
  const summed = new Uint32Array(stride ** 2)
  const states = ENGINEERING_MODELS.map(() => Array.from({ length: 5 }, () => new Uint8Array(IMAGE_SIZE ** 2)))
  let transition: { cycle: number; moves: BlockMove[] } | undefined

  function cells(model: number, progress: number, time: number, emit: (x: number, y: number, size: number) => void) {
    const mask = rasterize!(model, time)
    summed.fill(0)
    for (let y = 0; y < IMAGE_SIZE; y++) {
      let row = 0
      for (let x = 0; x < IMAGE_SIZE; x++) {
        row += mask[y * IMAGE_SIZE + x]
        summed[(y + 1) * stride + x + 1] = summed[y * stride + x + 1] + row
      }
    }
    function cell(x: number, y: number, size: number) {
      const count = summed[(y + size) * stride + x + size] - summed[y * stride + x + size]
        - summed[(y + size) * stride + x] + summed[y * stride + x]
      if (!count) return
      const index = y * IMAGE_SIZE + x
      const seed = seeds[index]
      // A broad front rises through the object, leaving several resolutions visible together.
      const local = smooth((progress - (1 - y / IMAGE_SIZE) * .35) / .65)
      const level = local * 4.4 + (seed - .5) * .45
      if (size > 1 && Math.log2(size) > level) {
        const half = size / 2
        cell(x, y, half); cell(x + half, y, half)
        cell(x, y + half, half); cell(x + half, y + half, half)
        return
      }
      const state = states[model][Math.log2(size)]
      const threshold = size === 1 ? 0 : .18 + seed * .42 + (state[index] ? -.025 : .025)
      state[index] = count / (size * size) > threshold ? 1 : 0
      if (!state[index]) return
      const inset = size > 2 ? size * .12 * local : 0
      emit(x + size / 2, y + size / 2, size - inset * 2)
    }
    for (let y = 0; y < IMAGE_SIZE; y += MAX_CELL) {
      for (let x = 0; x < IMAGE_SIZE; x += MAX_CELL) cell(x, y, MAX_CELL)
    }
  }
  function capture(model: number, time: number) {
    const blocks: Block[] = []
    cells(model, 1, time, (x, y, size) => blocks.push({ x, y, size }))
    return blocks
  }

  return (ctx: CanvasRenderingContext2D, width: number, height: number, time: number, color: string) => {
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = color
    const cycle = Math.floor(time / CYCLE)
    const current = cycle % ENGINEERING_MODELS.length
    const next = (current + 1) % ENGINEERING_MODELS.length
    const phase = time % CYCLE
    const diameter = Math.min(width * (width < 700 ? 1.55 : .94), height * 1.02, 1100)
    const scale = diameter / IMAGE_SIZE
    const left = (width - diameter) / 2
    const top = height * .6 - diameter / 2
    function paint(x: number, y: number, size: number, opacity = 1) {
      ctx.globalAlpha = .48 * opacity
      const px = Math.round(left + (x - size / 2) * scale)
      const py = Math.round(top + (y - size / 2) * scale)
      ctx.fillRect(px, py, Math.round(left + (x + size / 2) * scale) - px, Math.round(top + (y + size / 2) * scale) - py)
    }

    if (phase < COARSE_AT) {
      cells(current, smooth((phase - 3) / (COARSE_AT - 3)), time, paint)
    } else if (phase <= MORPH_END) {
      if (transition?.cycle !== cycle) {
        // Freeze both coarse poses for this handoff so matching never reshuffles mid-flight.
        const start = cycle * CYCLE
        transition = { cycle, moves: matchBlocks(capture(current, start + COARSE_AT), capture(next, start + MORPH_END)) }
      }
      const progress = smooth((phase - MORPH_START) / (MORPH_END - MORPH_START))
      for (const { from, to } of transition.moves) {
        if (to) paint(mix(from.x, to.x, progress), mix(from.y, to.y, progress), mix(from.size, to.size, progress))
        else paint(from.x, from.y, from.size * (1 - progress), 1 - progress)
      }
    } else {
      cells(next, 1 - smooth((phase - MORPH_END) / 7), time, paint)
    }
    ctx.globalAlpha = 1
  }
}
